import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Wrench, MapPin, Phone, Pencil, Trash2, Plus } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/Toronto", dateStyle: "medium" }) : "—");
const REC = { yes: ["Recommended", "bg-emerald-50 text-emerald-800 border-emerald-200"], maybe: ["It was OK", "bg-amber-50 text-amber-800 border-amber-200"], no: ["Not recommended", "bg-rose-50 text-rose-800 border-rose-200"] };
const FIELDS = [["shop_name", "Shop / garage"], ["mechanic_name", "Mechanic"], ["address", "Address"], ["city", "City"], ["province", "Province / State"], ["country", "Country"], ["phone", "Phone"], ["email", "E-mail"]];

// Installers tab — mechanics / shops reported by customers in the review e-mail form, grouped per city.
export default function Installers() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [by, setBy] = useState("mechanic");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const staff = ["back_office", "super_admin"].includes(user?.role);
  const { data, isLoading, error } = useQuery({ queryKey: ["installers", by, q], queryFn: async () => (await api.get(`/installers?by=${by}&q=${encodeURIComponent(q)}`)).data, enabled: staff });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/installers/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["installers"] }); toast.success("Removed"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not remove"),
  });
  if (!staff) return <Navigate to="/" replace />;
  const groups = data?.groups || [];
  const counts = data?.counts || {};
  return (
    <div className="p-6 space-y-4" data-testid="installers-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight inline-flex items-center gap-2"><Wrench size={22} /> Installers <span className="text-slate-400 font-normal text-base">· per city</span></h1>
          <p className="text-sm text-slate-500">Mechanics and shops our customers used to install their covers — collected from the “Who installed your covers?” form in the review e-mail. {counts.mechanic || 0} shops in {counts.cities || 0} cities · {counts.self || 0} self-installs.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search city, shop, phone, PO…" className="h-9 w-56" data-testid="installers-search" />
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => setEdit({ installed_by: "mechanic" })} data-testid="installers-add-btn"><Plus size={14} /> Add installer</Button>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap" data-testid="installers-folders">
        {[["mechanic", `Mechanics / shops (${counts.mechanic || 0})`], ["self", `Self-installed (${counts.self || 0})`], ["all", `All (${counts.total || 0})`]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setBy(k)} data-testid={`installers-folder-${k}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${by === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}>{l}</button>
        ))}
      </div>
      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {error && <div className="text-sm text-rose-700" data-testid="installers-error">{error?.response?.data?.detail || "Could not load installers"}</div>}
      {!isLoading && !error && groups.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="installers-empty">
          Nothing here yet. Entries appear when customers answer the installer form in the review e-mail (3 weeks after fulfilment), or add one manually.
        </div>
      )}
      <div className="space-y-5">
        {groups.map((g) => (
          <section key={g.city} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`installers-city-${g.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <header className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
              <MapPin size={16} className="text-slate-500" />
              <h2 className="font-semibold text-base">{g.city}{g.province ? <span className="text-slate-500 font-normal">, {g.province}</span> : null}</h2>
              <span className="text-xs text-slate-500">· {g.mechanics} shop{g.mechanics === 1 ? "" : "s"}{g.self ? ` · ${g.self} self-install${g.self === 1 ? "" : "s"}` : ""}</span>
            </header>
            <ul className="divide-y divide-slate-100">
              {g.items.map((it) => {
                const rec = REC[it.recommend];
                return (
                  <li key={it.id} className="px-4 py-3 flex flex-wrap gap-x-6 gap-y-1 items-start" data-testid={`installer-row-${it.id}`}>
                    <div className="min-w-[220px] flex-1">
                      <div className="font-semibold text-sm">{it.installed_by === "self" ? "Self-installed" : it.shop_name || it.mechanic_name || "Mechanic"}{it.installed_by === "mechanic" && it.shop_name && it.mechanic_name ? <span className="text-slate-500 font-normal"> · {it.mechanic_name}</span> : null}</div>
                      {it.installed_by === "mechanic" && (
                        <div className="text-xs text-slate-600 flex flex-wrap gap-x-3">
                          {it.address && <span>{it.address}</span>}
                          {it.phone && <a href={`tel:${it.phone}`} className="inline-flex items-center gap-1 underline"><Phone size={11} /> {it.phone}</a>}
                          {it.verified && <Badge className="bg-sky-50 text-sky-800 border border-sky-200 text-[10px]">Verified by LPC</Badge>}
                        </div>
                      )}
                      {it.notes && <div className="text-xs italic text-slate-600 mt-0.5">“{it.notes}”</div>}
                      {it.staff_notes && <div className="text-xs text-slate-500 mt-0.5">Staff: {it.staff_notes}</div>}
                    </div>
                    <div className="text-xs text-slate-600 min-w-[200px]">
                      {rec && <Badge className={`${rec[1]} border text-[10px] mr-2`}>{rec[0]}</Badge>}
                      {it.po_number && <Link to={`/order/${it.order_id}`} className="underline text-sky-700">{it.po_number}</Link>}
                      {it.vehicle && <span> · {it.vehicle}</span>}
                      {it.customer_name && <div>{it.customer_name}</div>}
                      <div className="text-slate-400">{fmt(it.created_at)}{(it.photo_ids || []).length ? ` · ${it.photo_ids.length} photo${it.photo_ids.length > 1 ? "s" : ""}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-1 ml-auto">
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => setEdit(it)} data-testid={`installer-edit-${it.id}`}><Pencil size={14} /></Button>
                      <Button size="sm" variant="ghost" className="h-8 text-rose-700" onClick={() => { if (window.confirm("Remove this installer entry?")) del.mutate(it.id); }} data-testid={`installer-delete-${it.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
      {edit && <InstallerDialog item={edit} onClose={() => setEdit(null)} />}
    </div>
  );
}

function InstallerDialog({ item, onClose }) {
  const qc = useQueryClient();
  const isNew = !item.id;
  const [f, setF] = useState({ recommend: "", notes: "", staff_notes: "", ...item });
  const save = useMutation({
    mutationFn: async () => {
      const body = {};
      [...FIELDS.map(([k]) => k), "recommend", "notes", "staff_notes"].forEach((k) => { if (f[k] !== undefined && f[k] !== null) body[k] = f[k]; });
      body.verified = !!f.verified;
      if (isNew) return (await api.post("/installers", { ...body, installed_by: f.installed_by || "mechanic" })).data;
      return (await api.patch(`/installers/${item.id}`, body)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["installers"] }); toast.success(isNew ? "Installer added" : "Saved"); onClose(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save"),
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="installer-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add installer" : "Edit installer"}</DialogTitle>
          <DialogDescription>{isNew ? "A shop or mechanic you want on the list." : `Reported ${fmt(item.created_at)}${item.po_number ? ` · ${item.po_number}` : ""}`}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => (
            <div key={k} className={k === "address" ? "col-span-2" : ""}>
              <Label className="text-xs">{l}</Label>
              <Input value={f[k] || ""} onChange={set(k)} className="h-9 text-sm" data-testid={`installer-field-${k.replace(/_/g, "-")}`} />
            </div>
          ))}
          <div>
            <Label className="text-xs">Recommended by customer</Label>
            <select value={f.recommend || ""} onChange={set("recommend")} className="h-9 w-full rounded-md border border-slate-300 text-sm px-2" data-testid="installer-field-recommend">
              <option value="">—</option><option value="yes">Yes</option><option value="maybe">It was OK</option><option value="no">No</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm mt-5"><input type="checkbox" checked={!!f.verified} onChange={(e) => setF((p) => ({ ...p, verified: e.target.checked }))} data-testid="installer-field-verified" /> Verified by LPC</label>
          <div className="col-span-2">
            <Label className="text-xs">Customer's note</Label>
            <Input value={f.notes || ""} onChange={set("notes")} className="h-9 text-sm" data-testid="installer-field-notes" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Staff notes (internal)</Label>
            <Input value={f.staff_notes || ""} onChange={set("staff_notes")} className="h-9 text-sm" data-testid="installer-field-staff-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-slate-900 hover:bg-slate-800 text-white" disabled={save.isPending} onClick={() => save.mutate()} data-testid="installer-dialog-save">{save.isPending ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
